/**
 * Commodity-aware display helpers for multi-deal desks (Eagle gas · Juno/Sedgwick oil).
 * Prefer registry primaryProduct when present; fall back to whichever price field exists.
 */

/** True when deal/meta marks oil as primary (Sedgwick oil+gas+ngl still oil-primary). */
export function isOilPrimary(dealOrMeta) {
  if (!dealOrMeta) return false;
  return (dealOrMeta.primaryProduct || dealOrMeta.product) === 'oil';
}

/** True when registry/meta commodity or products include gas (Eagle gas, Sedgwick oil+gas+ngl). */
export function hasGasCommodity(dealOrMeta) {
  if (!dealOrMeta) return false;
  const primary = dealOrMeta.primaryProduct || dealOrMeta.product;
  if (primary === 'gas') return true;
  const commodity = String(dealOrMeta.commodity || '').toLowerCase();
  if (commodity.includes('gas')) return true;
  const products = dealOrMeta.products;
  if (Array.isArray(products)) {
    return products.some((p) => {
      const id = String(p).toLowerCase();
      return (
        id === 'gas' ||
        id === 'casinghead_gas' ||
        id === 'casinghead' ||
        id === 'residue_gas' ||
        id === 'residue'
      );
    });
  }
  return false;
}

/** True when any row has a finite non-zero volume on one of the given keys. */
export function rowsHavePositiveVolume(rows = [], keys = []) {
  const keyList = Array.isArray(keys) ? keys : [keys];
  return (Array.isArray(rows) ? rows : []).some((row) =>
    keyList.some((key) => {
      const n = Number(row?.[key]);
      return Number.isFinite(n) && n !== 0;
    }),
  );
}

/**
 * Show Net MMcf / gas volume columns when the deal is gas-capable or rows carry gas volumes.
 * Oil-only desks (e.g. Juno with zero gas) stay oil-only; Sedgwick oil+gas+ngl shows both.
 */
export function shouldShowGasVolumes(dealOrMeta, rows = [], gasKeys = ['netGasMmcf', 'netMmcf']) {
  if (hasGasCommodity(dealOrMeta)) return true;
  return rowsHavePositiveVolume(rows, gasKeys);
}

/**
 * Pick price-deck series key + labels from rows and/or deal registry.
 * Oil → WTI when `wti` present; gas → HH when `hh` present; else first available.
 */
export function resolvePriceDeckSeries(priceDeck = [], dealOrMeta = null) {
  const rows = Array.isArray(priceDeck) ? priceDeck : [];
  const sample = rows.find((r) => r && (r.wti != null || r.hh != null)) || rows[0] || {};
  const oil = isOilPrimary(dealOrMeta);
  const hasWti = sample.wti != null || rows.some((r) => r?.wti != null);
  const hasHh = sample.hh != null || rows.some((r) => r?.hh != null);

  if (oil && hasWti) {
    return { yKey: 'wti', shortLabel: 'WTI', subtitle: 'WTI $/bbl · loaded into model', unit: '/bbl' };
  }
  if (!oil && hasHh) {
    return {
      yKey: 'hh',
      shortLabel: 'Henry Hub',
      subtitle: 'Henry Hub $/Mcf · loaded into the seller forecast · click bar for detail',
      unit: '/Mcf',
    };
  }
  if (hasWti) {
    return { yKey: 'wti', shortLabel: 'WTI', subtitle: 'WTI $/bbl · loaded into model', unit: '/bbl' };
  }
  if (hasHh) {
    return {
      yKey: 'hh',
      shortLabel: 'Henry Hub',
      subtitle: 'Henry Hub $/Mcf · loaded into the seller forecast · click bar for detail',
      unit: '/Mcf',
    };
  }
  return {
    yKey: oil ? 'wti' : 'hh',
    shortLabel: oil ? 'WTI' : 'Henry Hub',
    subtitle: oil ? 'WTI $/bbl' : 'Henry Hub $/Mcf',
    unit: oil ? '/bbl' : '/Mcf',
  };
}

/** Monthly LOS volume column. Oil desks use barrels; gas desks stay on Mcf. */
export function cashflowVolumeColumn(meta) {
  return isOilPrimary(meta)
    ? { key: 'oilVolumeBbl', label: 'Volume (bbl)' }
    : { key: 'gasVolumeMcf', label: 'Volume (Mcf)' };
}

/** Chart title for the realized-price series. Gas copy stays "gas". */
export function realizedPriceChartTitle(meta) {
  return isOilPrimary(meta)
    ? 'Realized oil price after deducts'
    : 'Realized gas price after deducts';
}

/**
 * Seller cumulative already stored in the header unit (Mbbl or MMcf).
 * Oil workflows read oilMbbl. Gas workflows keep gasMmcf.
 */
export function sellerCumToDate(workflow, cums) {
  if (!cums) return null;
  const raw = isOilPrimary(workflow) ? cums.oilMbbl : cums.gasMmcf;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/** Prefer gasDiff, else oilDiff; returns { value, unit } with null value when neither set. */
export function resolveDifferential(row = {}) {
  if (row.gasDiff != null && !Number.isNaN(Number(row.gasDiff))) {
    return { value: Number(row.gasDiff), unit: '/Mcf' };
  }
  if (row.oilDiff != null && !Number.isNaN(Number(row.oilDiff))) {
    return { value: Number(row.oilDiff), unit: '/bbl' };
  }
  return { value: null, unit: '' };
}
